import { Component, OnInit, ViewChild, ElementRef, Output, EventEmitter, Input } from '@angular/core';
import { Store, Actions, ofActionSuccessful } from '@ngxs/store';
import {
  NgbDate,
  NgbDateStruct,
  NgbCalendar,
  NgbDateParserFormatter,
  NgbDatepickerI18n, NgbInputDatepicker
} from '@ng-bootstrap/ng-bootstrap';
import {
  AuctionItemSearchPayload,
  AuctionItemSell,
  SearchResultAuctionItemMetadata,
  StatusFilter,
  DateUtilsService,
  SellSearchState,
  SearchAuctionItems,
  SetDateFilters,
  DateTimeFormat,
  LaunchDarklyService,
  DateFormatterService
} from 'remarketing-angular-library';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslocoDirective } from '@jsverse/transloco';
import { TrackDirective } from 'remarketing-ui-components-lib';


@Component({
    selector: 'lib-utility-bar-date-range',
    templateUrl: './utility-bar-date-range.component.html',
    styleUrls: ['./utility-bar-date-range.component.scss'],
    providers: [{ provide: NgbDateParserFormatter, useClass: DateFormatterService }],
    imports: [TranslocoDirective, ReactiveFormsModule, NgbInputDatepicker, TrackDirective]
})
export class UtilityBarDateRangeComponent implements OnInit {
  @ViewChild('fromCalendar') fromCalendarButton: ElementRef;
  @ViewChild('toCalendar') toCalendarButton: ElementRef;
  @Output() hasDateRangeError = new EventEmitter<boolean>(false);
  @Input() trackCalendars = false;

  fromFormControl = new UntypedFormControl();
  toFormControl = new UntypedFormControl();

  fromMinDate: NgbDateStruct;
  fromMaxDate: NgbDateStruct;
  toMaxDate: NgbDateStruct;
  toMinDate: NgbDateStruct;
  toStartDate: NgbDateStruct;
  fromStartDate: NgbDateStruct;
  today: NgbDateStruct = {
    year: this.ngbCalendar.getToday().year,
    month: this.ngbCalendar.getToday().month,
    day: this.ngbCalendar.getToday().day
  };

  fromDateValid = true;
  toDateValid = true;

  displayFromDate: string;
  displayToDate: string;

  private destroyed$ = new Subject();

  get fromDateTrack() {
    return this.trackCalendars
      ? UtilityBarDateRangeComponent.calendarTrackData('From Date')
      : { input: {}, button: {} };
  }

  get toDateTrack() {
    return this.trackCalendars ? UtilityBarDateRangeComponent.calendarTrackData('To Date') : { input: {}, button: {} };
  }

  get isCanadianTenant() {
    return this.launchDarklyService.getFeatureFlag('canadianTenantEnabled', false);
  }

  constructor(
    private readonly store: Store,
    private ngbCalendar: NgbCalendar,
    public i18n: NgbDatepickerI18n,
    public dateFormatter: DateFormatterService,
    private readonly actions$: Actions,
    private readonly dateUtilsService: DateUtilsService,
    private readonly launchDarklyService: LaunchDarklyService
  ) {}

  private static calendarTrackData(name: string) {
    return {
      input: { name: `${name}`, elem: 'calendar' },
      button: { name: `${name}`, elem: 'calendar' }
    };
  }

  ngOnInit() {
    this.setInitialValues();

    this.actions$
      .pipe(ofActionSuccessful(SearchAuctionItems), takeUntil(this.destroyed$))
      .subscribe(({ payload }: SearchAuctionItems) => {
        this.calculateDates(payload);

        this.fromDateValid = true;
        this.toDateValid = true;
      });
  }

  // Handles the logic for setting minimum and maximum allowed date values for the date picker
  setMinMaxDateValues(
    payload: AuctionItemSearchPayload,
    metadata: SearchResultAuctionItemMetadata,
    updatedFromDate: NgbDateStruct | undefined
  ) {
    // Removed Vehicle Search response includes a maxFromDate value, which equates to the fromMinDate here (earliest valid date)
    if (payload && payload.filterByStatus === StatusFilter.REMOVED_VEHICLES) {
      this.fromMinDate = this.dateFormatter.parse(metadata.maxFromDate);
    } else {
      this.fromMinDate = { year: this.today.year - 1, month: this.today.month, day: this.today.day };
    }

    this.toMinDate = updatedFromDate ? updatedFromDate : this.fromMinDate;
  }

  private calculateDates(payload: AuctionItemSearchPayload) {
    let data: AuctionItemSell[];
    let metadata: { fromDate?: string; toDate?: string; [key: string]: any; maxFromDate?: string };

    if (this.store.selectSnapshot(SellSearchState.searchResponse)) {
      ({ data, metadata } = this.store.selectSnapshot(SellSearchState.searchResponse));
    }

    const updatedFromDate = this.getFromDate(payload, data, metadata);
    const updatedToDate = this.getToDate(payload, data, metadata);

    this.toFormControl.setValue(updatedToDate, { emitEvent: false });
    this.fromFormControl.setValue(updatedFromDate, { emitEvent: false });

    this.fromStartDate = updatedFromDate;
    this.toStartDate = updatedToDate;

    this.setMinMaxDateValues(payload, metadata, updatedFromDate);

    if (payload && payload.vinList && payload.vinList.length !== 0 && metadata) {
      this.store.dispatch(
        new SetDateFilters(
          this.dateFormatter.formatPayload(updatedFromDate),
          this.dateFormatter.formatPayload(updatedToDate)
        )
      );
    }
    this.displayFromDate = this.dateFormatter.localizeDateToString(updatedFromDate);
    this.displayToDate = this.dateFormatter.localizeDateToString(updatedToDate);
  }

  private getFromDate(
    payload: AuctionItemSearchPayload,
    data: AuctionItemSell[],
    metadata: { fromDate?: string; toDate?: string; [key: string]: any }
  ): NgbDateStruct | undefined {
    // blank search
    if (payload && !payload.vin && !payload.stockNumber && (!payload.vinList || !payload.vinList.length)) {
      // persist fromDate
      if (payload.fromDate) {
        return this.dateFormatter.parse(payload.fromDate);
      }
      // reset fromDate
      else {
        if (payload.filterByStatus === StatusFilter.ACTIVE_VEHICLES) {
          // If the user click on the ACTIVE radio button, the dates will be set to be undefined
          return undefined;
        } else {
          // If the user performs a blank search (clearing VIN/Stock# or clearing multiple VIN
          // then clicking Search button), the dates will be set to bring back the last 7 days of sold vehicles
          return this.ngbCalendar.getPrev(NgbDate.from(this.today), 'd', 7);
        }
      }
    }
    // multi vin search
    else if (payload && payload.vinList && payload.vinList.length !== 0 && metadata) {
      return this.dateFormatter.parse(metadata.fromDate);
    }
    // single vin search or stock number search
    else {
      if (data && data.length !== 0) {
        // If the user performs a VIN search, then set "From Date" to the vehicle's earliest sale date
        let saleDateData;
        data.forEach(d => (saleDateData = d.saleDate));
        const minDate = this.dateUtilsService.formatDate(saleDateData, DateTimeFormat.MM_DD_YYYY, true);
        return this.dateFormatter.parse(minDate);
      }
    }
    return undefined;
  }

  private getToDate(
    payload: AuctionItemSearchPayload,
    data: AuctionItemSell[],
    metadata: { fromDate?: string; toDate?: string; [key: string]: any }
  ): NgbDateStruct | undefined {
    // blank search
    if (payload && !payload.vin && !payload.stockNumber && (!payload.vinList || !payload.vinList.length)) {
      // persist fromDate
      if (payload.toDate) {
        return this.dateFormatter.parse(payload.toDate);
      }
      // reset fromDate
      else {
        // If the user performs a blank search (clearing VIN/Stock# or clearing multiple VIN
        // then clicking Search button), the dates will be set to bring back the last 7 days of sold vehicles
        return this.today;
      }
    }
    // multi vin search
    else if (payload && payload.vinList && payload.vinList.length !== 0 && metadata) {
      return this.dateFormatter.parse(metadata.toDate);
    }
    // single vin search or stock number search
    else {
      if (data && data.length !== 0) {
        // If the user performs a VIN search, then set "To Date" to the vehicle's latest sale date
        let saleDateData;
        data.forEach(d => (saleDateData = d.saleDate));
        const maxDate = this.dateUtilsService.formatDate(saleDateData, DateTimeFormat.MM_DD_YYYY, false);
        return this.dateFormatter.parse(maxDate);
      }
    }
    return undefined;
  }

  setInitialValues() {
    this.toMaxDate = this.today;
    this.fromMinDate = { year: this.today.year - 1, month: this.today.month, day: this.today.day };

    const existingRequest = this.store.selectSnapshot(SellSearchState.searchRequest);

    if (existingRequest && existingRequest.toDate) {
      const parsedToDate = this.dateFormatter.parse(existingRequest.toDate);
      this.toFormControl.setValue(parsedToDate, { emitEvent: false });
      this.toStartDate = parsedToDate;
      this.fromMaxDate = parsedToDate;
    } else {
      this.toFormControl.setValue(this.today, { emitEvent: false });
      this.toStartDate = this.today;
      this.fromMaxDate = this.today;
    }

    if (existingRequest && existingRequest.fromDate) {
      const parsedFromDate = this.dateFormatter.parse(existingRequest.fromDate);
      this.fromFormControl.setValue(parsedFromDate, { emitEvent: false });
      this.displayFromDate = this.dateFormatter.localizeDateToString(parsedFromDate);
      this.fromStartDate = parsedFromDate;
      this.toMinDate = parsedFromDate;
    } else {
      // By default, the dates will be set to bring back the last 7 days of sold vehicles
      let defaultFromDate = this.ngbCalendar.getPrev(NgbDate.from(this.today), 'd', 7);
      if (existingRequest && existingRequest.filterByStatus === StatusFilter.ACTIVE_VEHICLES) {
        // By default, the dates will be set to undefined of sold vehicles
        defaultFromDate = undefined;
      }

      this.fromFormControl.setValue(defaultFromDate, { emitEvent: false });
      this.fromStartDate = defaultFromDate;
      this.toMinDate = this.fromMinDate;
    }

    this.calculateDates(existingRequest);
  }

  updateFromDate(fromDate: NgbDate | NgbDateStruct) {
    const fromValue = NgbDate.from(this.dateFormatter.sanitizeDateInput(fromDate));
    this.toMinDate = fromValue ? fromValue : this.fromMinDate;

    if (fromDate && this.validateFromValue()) {
      const currentRequest = this.store.selectSnapshot(SellSearchState.searchRequest);

      this.fromStartDate = fromValue ? fromValue : this.fromStartDate;
      this.displayFromDate = this.dateFormatter.localizeDateToString(this.fromStartDate);
      this.fromDateValid = true;
      this.toDateValid = this.validateToValue();

      const sanitizedToValue = this.dateFormatter.sanitizeDateInput(this.toFormControl.value);

      if (this.toDateValid && this.isValidDateRange()) {
        this.toDateValid = true;
        this.updateOfferDateFilter(fromValue, NgbDate.from(sanitizedToValue), currentRequest);
      }
    } else {
      this.fromDateValid = false;
    }
    this.hasDateRangeError.emit(!(this.fromDateValid && this.toDateValid));
  }

  updateToDate(toDate: NgbDate | NgbDateStruct) {
    const toValue = NgbDate.from(this.dateFormatter.sanitizeDateInput(toDate));

    this.fromMaxDate = toValue ? toValue : this.today;

    if (toDate && this.validateToValue()) {
      const currentRequest = this.store.selectSnapshot(SellSearchState.searchRequest);

      this.toStartDate = toValue;
      this.displayToDate = this.dateFormatter.localizeDateToString(this.toStartDate);
      this.toDateValid = true;
      this.fromDateValid = this.validateFromValue();

      const sanitizedFromValue = this.dateFormatter.sanitizeDateInput(this.fromFormControl.value);

      if (this.fromDateValid && this.isValidDateRange()) {
        this.fromDateValid = true;
        this.updateOfferDateFilter(NgbDate.from(sanitizedFromValue), toValue, currentRequest);
      }
    } else {
      this.toDateValid = false;
    }
    this.hasDateRangeError.emit(!(this.fromDateValid && this.toDateValid));
  }

  private isValidDateRange(): boolean {
    if (this.fromFormControl.value === undefined) return true;

    const toDate = new NgbDate(
      this.toFormControl.value.year,
      this.toFormControl.value.month,
      this.toFormControl.value.day
    );

    return toDate.after(this.fromFormControl.value) || toDate.equals(this.fromFormControl.value);
  }

  validateFromValue(): boolean {
    const toDateValue = this.dateFormatter.sanitizeDateInput(this.toFormControl.value);
    const fromDateValue = this.dateFormatter.sanitizeDateInput(this.fromFormControl.value);

    if (toDateValue === undefined && fromDateValue === undefined) return true;

    if (fromDateValue && fromDateValue.month !== null && fromDateValue.day !== null && fromDateValue.year !== null) {
      const fromDate = new NgbDate(fromDateValue.year, fromDateValue.month, fromDateValue.day);

      return (
        this.ngbCalendar.isValid(fromDate) &&
        (fromDate.after(this.fromMinDate) || fromDate.equals(this.fromMinDate)) &&
        (fromDate.before(this.fromMaxDate) || fromDate.equals(this.fromMaxDate))
      );
    } else {
      return false;
    }
  }

  validateToValue(): boolean {
    const toDateValue = this.dateFormatter.sanitizeDateInput(this.toFormControl.value);
    const fromDateValue = this.dateFormatter.sanitizeDateInput(this.fromFormControl.value);

    if (toDateValue === undefined && fromDateValue === undefined) return true;

    if (toDateValue && toDateValue.month !== null && toDateValue.day !== null && toDateValue.year !== null) {
      const toDate = new NgbDate(toDateValue.year, toDateValue.month, toDateValue.day);

      // if the from date is undefined (e.g. default from date for ACTIVE vehicles), just check if the to date is valid or undefined
      if (fromDateValue === undefined) return this.ngbCalendar.isValid(toDate);

      return (
        this.ngbCalendar.isValid(toDate) &&
        (toDate.after(this.toMinDate) || toDate.equals(this.toMinDate)) &&
        (toDate.before(this.toMaxDate) || toDate.equals(this.toMaxDate))
      );
    } else {
      return false;
    }
  }

  updateOfferDateFilter(fromDate: NgbDate, toDate: NgbDate, currentRequest: AuctionItemSearchPayload) {
    const formattedFromDate = this.dateFormatter.formatPayload(fromDate);
    const formattedToDate = this.dateFormatter.formatPayload(toDate);

    this.displayFromDate = this.localizeDate(formattedFromDate);
    this.displayToDate = this.localizeDate(formattedToDate);

    const payload = {
      ...currentRequest,
      vin: '',
      stockNumber: '',
      page: 0,
      fromDate: formattedFromDate,
      toDate: formattedToDate
    };

    this.store.dispatch(new SearchAuctionItems(payload));
  }

  localizeDate(date: any): string {
    return this.dateUtilsService.localizeDate(date);
  }

  focusFromCalendar() {
    this.fromCalendarButton.nativeElement.focus();
  }

  focusToCalendar() {
    this.toCalendarButton.nativeElement.focus();
  }

  ngOnDestroy(): void {
    this.hasDateRangeError.emit(false);
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
